import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, map, mergeMap, of, switchMap } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { AppState } from 'src/app/core/store/state';
import { DialogsActions } from '../../dialogs/store/actions';
import { NotificationsActions } from '../../notifications/store/actions';
import { PartiesApiService } from '../services/parties-api.service';
import { CounterpartiesActions } from './actions';

@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private partiesApiService: PartiesApiService,
    private dialogRef: MatDialog,
    private translocoService: TranslocoService,
  ) {}

  getCounterparties$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(CounterpartiesActions.getCounterparties))]).pipe(
      map(() => {
        return CounterpartiesActions.loadCounterparties();
      }),
    ),
  );

  loadCounterparties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.loadCounterparties),
      switchMap(() => {
        return this.partiesApiService.getParties$().pipe(
          map((counterparties) => CounterpartiesActions.setCounterparties({ counterparties })),
          catchError((response) => {
            return of(CounterpartiesActions.loadCounterpartiesFail());
          }),
        );
      }),
    ),
  );

  createCounterparty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.createCounterparty),
      mergeMap((props) =>
        this.partiesApiService.createParty$(props.model.name).pipe(
          map((counterparty) => CounterpartiesActions.createCounterpartySuccess({ counterparty })),
          catchError((response) => {
            return of(
              CounterpartiesActions.createCounterpartyFail(),
              DialogsActions.showDialog({
                data: {
                  actionsKeys: ['close'],
                  content: '',
                  title: this.translocoService.translate('errors.createCounterparty'),
                },
              }),
            );
          }),
        ),
      ),
    ),
  );

  createCounterpartySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.createCounterpartySuccess),
      map((props) => {
        this.store.dispatch(CounterpartiesActions.closeCreatingCounterpartyModal());
        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.counterpartyAdded', {
              name: props.counterparty.name,
            }),
          }),
        );

        this.dialogRef.closeAll();
        return CounterpartiesActions.getCounterparties();
      }),
    ),
  );

  createCounterpartyCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.createCounterpartyCancel),
      map((props) => {
        return CounterpartiesActions.createCounterpartyCancelled();
      }),
    ),
  );

  deleteCounterparty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterparty),
      map((props) => {
        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate('modalBody.confirmDeleteCounterparty'),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                CounterpartiesActions.deleteCounterpartyConfirmed({ id: props.id }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                CounterpartiesActions.deleteCounterpartyCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteCounterpartyConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterpartyConfirmed),
      mergeMap((props) => {
        return this.partiesApiService.deleteParty$(props.id).pipe(
          map(() => CounterpartiesActions.deleteCounterpartySuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              CounterpartiesActions.deleteCounterpartyFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteCounterpartySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterpartySuccess),
      map(() => {
        return CounterpartiesActions.getCounterparties();
      }),
    ),
  );

  deleteCounterparties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterparties),
      map((props) => {
        const titleKey =
          props.ids?.length === 1
            ? 'modalBody.confirmDeleteCounterparty'
            : 'modalBody.confirmDeleteCounterparties';

        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate(titleKey),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                CounterpartiesActions.deleteCounterpartiesConfirmed({ ids: props.ids }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                CounterpartiesActions.deleteCounterpartiesCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteCounterpartiesConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterpartiesConfirmed),
      mergeMap((props) => {
        return this.partiesApiService.batchDeleteParties$(props.ids).pipe(
          map(() => CounterpartiesActions.deleteCounterpartiesSuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              CounterpartiesActions.deleteCounterpartiesFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteCounterpartiesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.deleteCounterpartiesSuccess),
      map(() => {
        return CounterpartiesActions.getCounterparties();
      }),
    ),
  );

  loadCounterparty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.loadCounterparty),
      switchMap(({ id }) => {
        return this.partiesApiService.getPartyById$(id).pipe(
          map((counterparty) => CounterpartiesActions.setCounterparty({ counterparty })),
          catchError((response) => {
            return of(CounterpartiesActions.loadCounterpartyFail());
          }),
        );
      }),
    ),
  );

  editCounterpartyCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.editCounterpartyCancel),
      map((props) => {
        return CounterpartiesActions.editCounterpartyCancelled();
      }),
    ),
  );

  editCounterparty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.editCounterparty),
      mergeMap((props) =>
        this.partiesApiService.updateParty$(props.model.name, props.id).pipe(
          map((counterparty) => CounterpartiesActions.editCounterpartySuccess({ counterparty })),
          catchError((response) => {
            return of(CounterpartiesActions.editCounterpartyFail());
          }),
        ),
      ),
    ),
  );

  editCounterpartySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CounterpartiesActions.editCounterpartySuccess),
      map((props) => {
        this.store.dispatch(CounterpartiesActions.closeEditingCounterpartyModal());
        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.partyUpdated', {
              name: props.counterparty.name,
            }),
          }),
        );

        this.dialogRef.closeAll();
        return CounterpartiesActions.getCounterparties();
      }),
    ),
  );
}
